/**
 * <CityAutocomplete /> — live-suggest city/town/village input.
 *
 * Queries `/api/geocode/city-suggest?q=&country=` (Nominatim-backed) with
 * 300ms debounce. Selecting a suggestion also auto-fills the state field.
 * Falls back to a plain input if the network is unreachable.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import { http } from "@/lib/api";

export default function CityAutocomplete({
  countryCode,
  value,
  onSelect,
  onChange,
  testid = "locality-city",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const timerRef = useRef(null);
  const boxRef = useRef(null);

  // Debounced fetch — 300ms so we don't hammer Nominatim (their policy
  // requires <= 1 req/sec anyway).
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const q = (value || "").trim();
    if (q.length < 2) { setSuggestions([]); return undefined; }
    timerRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const params = { q, limit: 8 };
        if (countryCode) params.country = countryCode;
        const { data } = await http.get("/geocode/city-suggest", { params });
        setSuggestions(data?.results || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setSuggestions([]);
      } finally { setBusy(false); }
    }, 300);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value, countryCode]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (s) => {
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    onSelect?.(s);
    onChange?.(s.city || value);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="block relative">
      <span className="text-sm font-semibold" style={{ color: "var(--wofy-text)" }}>
        City / Town / Village
      </span>
      <div className="relative">
        <input
          data-testid={`${testid}-input`}
          value={value}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => value && value.length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={countryCode ? "Type a city, town, or village…" : "Choose a country first"}
          disabled={!countryCode && !value}
          className="mt-1 w-full rounded-lg border px-4 py-3 text-base font-medium pr-10 disabled:opacity-60"
          style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
          autoComplete="off"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
        </span>
      </div>
      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border shadow-lg"
          style={{ background: "var(--wofy-bg)", borderColor: "var(--wofy-border)" }}
          role="listbox"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.city}-${s.state}-${s.country_code}-${i}`}
              role="option"
              aria-selected={i === highlight}
            >
              <button
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(s)}
                className={`w-full text-left px-4 py-3 text-base flex items-start gap-2 ${i === highlight ? "bg-black/[0.06]" : "hover:bg-black/[0.04]"}`}
                style={{ color: "var(--wofy-text)" }}
              >
                <MapPin className="w-4 h-4 mt-1 opacity-70" />
                <span className="flex-1">
                  <span className="font-semibold">{s.city}</span>
                  <span className="opacity-70 text-sm ml-1">
                    {[s.state, s.country].filter(Boolean).join(", ")}
                  </span>
                </span>
                {s.country_code && (
                  <span className="text-xs font-mono opacity-60 mt-1">{s.country_code}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
